import { useI18n } from '../i18n/i18n.jsx';
import { useTheme } from '../hooks/useTheme.jsx';
import { useState, useMemo } from 'react';
import Container from '../components/UI/Container';
import Section from '../components/UI/Section';
import SectionTitle from '../components/UI/SectionTitle';
import ContactFormModal from '../components/UI/ContactFormModal';

const Services = () => {
  const { t, language } = useI18n();
  const { isDark } = useTheme();
  const [activeCategory, setActiveCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSubject, setModalSubject] = useState('general');

  const serviceKeys = ['foreignPlacement', 'languageCourses', 'visaDocuments', 'flightAccommodation', 'studentConsulting'];
  
  const detailedServices = serviceKeys.map(key => ({
    key,
    icon: t(`services.detailedServices.${key}.icon`),
    title: t(`services.detailedServices.${key}.title`),
    description: t(`services.detailedServices.${key}.description`),
    highlight: t(`services.detailedServices.${key}.highlight`),
    whoIsThisFor: t(`services.detailedServices.${key}.whoIsThisFor`),
    features: t(`services.detailedServices.${key}.features`, { returnObjects: true })
  }));

  const tierKeys = ['essential', 'professional', 'premium'];
  
  const serviceTiers = tierKeys.map((key, index) => ({
    key,
    name: t(`services.serviceTiers.${key}.name`),
    price: t(`services.serviceTiers.${key}.price`),
    description: t(`services.serviceTiers.${key}.description`),
    features: t(`services.serviceTiers.${key}.features`, { returnObjects: true }),
    highlighted: key === 'professional'
  }));

  // FAQ Data - Using direct translation object access with returnObjects
  const faqData = useMemo(() => {
    const generalQuestions = t('services.faq.general.questions', { returnObjects: true });
    const admissionQuestions = t('services.faq.admission.questions', { returnObjects: true });
    const visaQuestions = t('services.faq.visa.questions', { returnObjects: true });
    const testsQuestions = t('services.faq.tests.questions', { returnObjects: true });
    const postQuestions = t('services.faq.post.questions', { returnObjects: true });

    return {
      title: t('services.faq.title'),
      subtitle: t('services.faq.subtitle'),
      allQuestions: t('services.faq.allQuestions'),
      categories: [
        {
          id: 'general',
          name: t('services.faq.general.name'),
          icon: t('services.faq.general.icon'),
          questions: generalQuestions || []
        },
        {
          id: 'admission',
          name: t('services.faq.admission.name'),
          icon: t('services.faq.admission.icon'),
          questions: admissionQuestions || []
        },
        {
          id: 'visa',
          name: t('services.faq.visa.name'),
          icon: t('services.faq.visa.icon'),
          questions: visaQuestions || []
        },
        {
          id: 'tests',
          name: t('services.faq.tests.name'),
          icon: t('services.faq.tests.icon'),
          questions: testsQuestions || []
        },
        {
          id: 'post',
          name: t('services.faq.post.name'),
          icon: t('services.faq.post.icon'),
          questions: postQuestions || []
        }
      ]
    };
  }, [language, t]);

  // Filter FAQs based on active category
  const filteredFaqs = activeCategory === 'all' 
    ? faqData.categories 
    : faqData.categories.filter(cat => cat.id === activeCategory);

  return (
    <main>
      {/* Hero Section */}
      <Section className="pt-24 sm:pt-32 pb-16 bg-neutral-light dark:bg-neutral-900/50">
        <Container>
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <div className="w-16 h-1 bg-accent mx-auto mb-6 rounded-full" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-primary dark:text-white mb-4">
              {t('services.title')}
            </h1>
            <p className="text-xl text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('services.subtitle')}
            </p>
          </div>
        </Container>
      </Section>

      {/* Detailed Services */}
      <Section>
        <Container>
          <div className="space-y-12">
            {detailedServices.map((service, index) => (
              <div
                key={index}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start p-8 bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700 hover:border-accent dark:hover:border-accent transition-all duration-300 hover:shadow-lg animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <span className="text-4xl" aria-hidden="true">{service.icon}</span>
                    <div>
                      <h3 className="text-2xl font-semibold text-primary dark:text-white mb-1">
                        {service.title}
                      </h3>
                      <span className="inline-block px-3 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full">
                        {service.highlight}
                      </span>
                    </div>
                  </div>
                  <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-300">
                        <svg className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:pl-8 border-l border-neutral-200 dark:border-neutral-700">
                  <div className="bg-neutral-light dark:bg-neutral-900/50 p-6 rounded-xl">
                    <h4 className="font-semibold text-primary dark:text-white mb-3">{t('services.whoIsThisForLabel')}</h4>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                      {service.whoIsThisFor}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Service Tiers/Pricing */}
      <Section className="bg-neutral-light dark:bg-neutral-900/50">
        <Container>
          <SectionTitle
            title={t('services.serviceTiers.title')}
            subtitle={t('services.serviceTiers.subtitle')}
          />
          
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {serviceTiers.map((tier, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-2xl border-2 ${
                  tier.highlighted
                    ? 'border-accent bg-white dark:bg-neutral-800 shadow-xl scale-105'
                    : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800'
                } transition-all duration-300 hover:shadow-xl`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-accent text-white text-xs font-bold px-3 py-1 rounded-full">
                      {t('services.serviceTiers.popular')}
                    </span>
                  </div>
                )}
                
                <h3 className="text-xl font-semibold text-primary dark:text-white mb-2">
                  {tier.name}
                </h3>
                <div className="text-3xl font-bold text-accent mb-2">
                  {tier.price}
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
                  {tier.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <svg className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-neutral-700 dark:text-neutral-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => {
                    setModalSubject('sales');
                    setIsModalOpen(true);
                  }}
                  className={`block w-full text-center py-3 rounded-lg font-medium transition-colors duration-200 ${
                    tier.highlighted
                      ? 'bg-primary text-white hover:bg-primary-light'
                      : 'bg-neutral-200 dark:bg-neutral-700 text-primary dark:text-white hover:bg-neutral-300 dark:hover:bg-neutral-600'
                  }`}
                >
                  {t('services.serviceTiers.getStarted')}
                </button>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* FAQ Section */}
      <Section>
        <Container>
          <SectionTitle
            title={faqData.title}
            subtitle={faqData.subtitle}
          />

          {/* Category Filter */}
          <div className="mt-8 flex flex-wrap gap-3 justify-center mb-12">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === 'all'
                  ? 'bg-primary text-white shadow-lg scale-105'
                  : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600'
              }`}
            >
              {faqData.allQuestions}
            </button>
            {faqData.categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeCategory === category.id
                    ? 'bg-primary text-white shadow-lg scale-105'
                    : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600'
                }`}
              >
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-8 max-w-4xl mx-auto">
            {filteredFaqs.map((category) => (
              <div
                key={category.id}
                className="animate-fade-in-up"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-2xl">{category.icon}</span>
                  <h3 className="text-2xl font-semibold text-primary dark:text-white">
                    {category.name}
                  </h3>
                </div>
                
                <div className="space-y-4">
                  {category.questions && category.questions.map((item, index) => (
                    <details
                      key={index}
                      className="group bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden transition-all duration-300 hover:border-accent dark:hover:border-accent"
                    >
                      <summary className="flex items-center justify-between p-5 cursor-pointer select-none">
                        <span className="font-medium text-primary dark:text-white pr-8">
                          {item.q}
                        </span>
                        <svg
                          className="w-5 h-5 text-accent flex-shrink-0 transition-transform duration-300 group-open:rotate-180"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </summary>
                      <div className="px-5 pb-5 pt-0 text-neutral-600 dark:text-neutral-300 leading-relaxed">
                        {item.a}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-16 max-w-4xl mx-auto text-center p-8 bg-primary/5 dark:bg-primary/10 rounded-2xl">
            <h3 className="text-2xl font-serif font-semibold text-primary dark:text-white mb-4">
              {t('services.contactCTA.title')}
            </h3>
            <p className="text-neutral-600 dark:text-neutral-300 mb-6">
              {t('services.contactCTA.subtitle')}
            </p>
            <button
              onClick={() => {
                setModalSubject('general');
                setIsModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-light transition-colors duration-200"
            >
              {t('services.contactCTA.button')}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </Container>
      </Section>

      {/* Additional Info */}
      <Section>
        <Container>
          <div className="max-w-4xl mx-auto text-center p-8 bg-primary/5 dark:bg-primary/10 rounded-2xl">
            <h3 className="text-2xl font-serif font-semibold text-primary dark:text-white mb-4">
              {t('services.contactCTA.customTitle')}
            </h3>
            <p className="text-neutral-600 dark:text-neutral-300 mb-6">
              {t('services.contactCTA.customSubtitle')}
            </p>
            <button
              onClick={() => {
                setModalSubject('general');
                setIsModalOpen(true);
              }}
              className="inline-flex items-center gap-2 text-primary dark:text-accent font-semibold hover:gap-3 transition-all duration-200"
            >
              {t('services.contactCTA.customButton')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </Container>
      </Section>
      
      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        defaultSubject={modalSubject}
      />
      
      {/* Bottom padding for mobile nav */}
      <div className="pb-24 md:pb-0"></div>
    </main>
  );
};

export default Services;